import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Users,
  GraduationCap,
  UserCheck,
  IndianRupee,
  AlertCircle,
  ClipboardList,
  Calendar,
  ArrowUpRight,
  ArrowDownRight,
  ChevronRight,
  Megaphone,
  School,
  MapPin,
  Phone,
  Mail
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { userApi, schoolDataApi, registrationApi } from '../services/api';
import { DashboardStats, SchoolRegistration, AdminUser, StudentRegistration } from '../types';
import { formatCurrency, formatNumber } from '../utils/helpers';
import { BarChart, Bar, XAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const getGreeting = (): string => {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
};

const getInitials = (name: string): string =>
  name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0]?.toUpperCase())
    .join('');

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [monthlyData, setMonthlyData] = useState<{ month: string; amount: number }[]>([]);
  const [school, setSchool] = useState<SchoolRegistration | null>(null);
  const [adminUser, setAdminUser] = useState<AdminUser | null>(null);
  const [recentRegistrations, setRecentRegistrations] = useState<StudentRegistration[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);

      // Get current user's school information
      const { adminUser: currentAdminUser, school: currentSchool } = await userApi.getCurrentUserSchool();

      if (!currentAdminUser) {
        setError('Admin user account not found. Please contact support or re-register your school.');
        console.error('Admin user not found for current authentication');
        return;
      }

      if (!currentSchool) {
        setError('School registration not found. Please contact support.');
        console.error('School not found for ID:', currentAdminUser.schoolId);
        return;
      }

      // Check if school registration is approved
      if (currentSchool.status === 'Pending') {
        setError('Your school registration is pending approval. Please wait for admin approval.');
        return;
      }

      if (currentSchool.status === 'Rejected') {
        const reason = currentSchool.rejectionReason ? `: ${currentSchool.rejectionReason}` : '';
        setError(`Your school registration was rejected${reason}. Please contact support.`);
        return;
      }

      setAdminUser(currentAdminUser);
      setSchool(currentSchool);

      // Get dashboard stats for the current school
      const dashboardStats = await schoolDataApi.getDashboardStats(currentAdminUser.schoolId);
      setStats(dashboardStats);

      // Get real monthly fee data for the current school
      const monthlyFeeData = await schoolDataApi.getMonthlyFeeData(currentAdminUser.schoolId);
      setMonthlyData(monthlyFeeData);

      // Get most recent admission registrations
      const registrations = await registrationApi.getAll();
      const sortedRegistrations = [...registrations]
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
        .slice(0, 4);
      setRecentRegistrations(sortedRegistrations);

    } catch (error) {
      setError('Failed to load dashboard statistics');
      console.error('Error loading dashboard stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const feeData = [
    { name: 'Collected', value: stats?.totalFeeCollection || 0, fill: '#4ade80' },
    { name: 'Pending', value: stats?.pendingFees || 0, fill: '#332b20' }
  ];

  const totalFees = (stats?.totalFeeCollection || 0) + (stats?.pendingFees || 0);
  const collectedPct = totalFees > 0 ? Math.round(((stats?.totalFeeCollection || 0) / totalFees) * 100) : 0;

  // Real month-over-month change for the monthly collection hero tile (null when there's no prior month to compare against)
  const currentMonthAmount = monthlyData[monthlyData.length - 1]?.amount ?? 0;
  const previousMonthAmount = monthlyData[monthlyData.length - 2]?.amount ?? 0;
  const monthlyChangePct = previousMonthAmount > 0
    ? Math.round(((currentMonthAmount - previousMonthAmount) / previousMonthAmount) * 100)
    : null;

  // Sparkline points for the monthly collection hero tile
  const sparkPoints = (() => {
    if (monthlyData.length === 0) return '';
    const amounts = monthlyData.map(d => d.amount);
    const min = Math.min(...amounts);
    const max = Math.max(...amounts);
    const range = max - min || 1;
    const stepX = 200 / Math.max(monthlyData.length - 1, 1);
    return monthlyData
      .map((d, i) => {
        const x = i * stepX;
        const y = 38 - ((d.amount - min) / range) * 32;
        return `${x.toFixed(1)},${y.toFixed(1)}`;
      })
      .join(' ');
  })();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#e2916d] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-[#341a17] border border-[#f38b83]/30 rounded-lg p-4">
        <div className="flex items-center space-x-2">
          <AlertCircle className="w-5 h-5 text-[#f38b83]" />
          <span className="text-[#f38b83]">{error}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-[#e2916d]">Dashboard</p>
          <h1 className="text-2xl font-bold text-[#f3ede2] mt-1">
            {getGreeting()}{school?.principalName ? `, ${school.principalName.split(' ')[0]}` : ''}
          </h1>
          <p className="text-[#b9ad9b] mt-1">
            Here's what's happening at {school?.schoolName || 'your school'} today.
          </p>
        </div>
        <div className="flex items-center space-x-2 text-sm text-[#b9ad9b] bg-[#211c15] border border-[#3a3122] rounded-full px-3 py-1.5 w-fit">
          <Calendar className="w-3.5 h-3.5" />
          <span>{new Date().toLocaleDateString('en-IN', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          })}</span>
        </div>
      </div>

      {/* School Information Strip */}
      {school && (
        <div className="flex flex-wrap items-center gap-3.5 bg-[#211c15] border border-[#3a3122] rounded-2xl px-4 py-3">
          <div className="w-10 h-10 bg-[#332217] rounded-xl flex items-center justify-center flex-shrink-0">
            {school.schoolLogoURL ? (
              <img
                src={school.schoolLogoURL}
                alt={school.schoolName}
                className="w-10 h-10 rounded-xl object-cover"
              />
            ) : (
              <School className="w-5 h-5 text-[#e2916d]" />
            )}
          </div>
          <span className="font-semibold text-[#f3ede2] text-sm">{school.schoolName}</span>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 sm:ml-auto text-xs text-[#b9ad9b]">
            <span className="flex items-center gap-1.5">
              <MapPin className="w-3.5 h-3.5 text-[#8a7f6e]" />
              {school.city}, {school.state}
            </span>
            <span className="flex items-center gap-1.5">
              <Mail className="w-3.5 h-3.5 text-[#8a7f6e]" />
              {school.adminEmail}
            </span>
            <span className="flex items-center gap-1.5">
              <Phone className="w-3.5 h-3.5 text-[#8a7f6e]" />
              {school.adminMobile}
            </span>
            <span>{school.affiliation} &middot; Est. {school.establishedYear}</span>
          </div>
        </div>
      )}

      {/* Bento Stats */}
      <div className="grid grid-cols-12 gap-3.5 auto-rows-[minmax(88px,auto)]">
        {/* Hero: Monthly Fee Collection */}
        <Link
          to="/admin/fees/collect"
          className="col-span-12 md:col-span-5 md:row-span-2 bg-[#e2916d] text-[#241408] rounded-2xl p-5 flex flex-col justify-between transition-transform duration-200 ease-snappy-out hover:-translate-y-0.5"
        >
          <div>
            <p className="text-sm font-medium text-[#241408]/80">Monthly Fee Collection</p>
            <p className="text-4xl font-bold tracking-tight mt-1.5 tabular-nums">
              {formatCurrency(stats?.monthlyFeeCollection || 0)}
            </p>
            {monthlyChangePct !== null && (
              <div className="inline-flex items-center gap-1 text-xs font-semibold bg-[#241408]/15 rounded-full px-2.5 py-1 mt-3">
                {monthlyChangePct >= 0 ? (
                  <ArrowUpRight className="w-3.5 h-3.5" />
                ) : (
                  <ArrowDownRight className="w-3.5 h-3.5" />
                )}
                {monthlyChangePct >= 0 ? '+' : ''}{monthlyChangePct}% vs last month
              </div>
            )}
          </div>
          {sparkPoints && (
            <svg viewBox="0 0 200 44" className="w-full h-11 mt-3" preserveAspectRatio="none">
              <polyline
                points={sparkPoints}
                fill="none"
                stroke="rgba(36,20,8,0.75)"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )}
        </Link>

        {/* Total Students */}
        <Link
          to="/admin/students"
          className="col-span-12 md:col-span-4 bg-[#211c15] border border-[#3a3122] rounded-2xl p-5 flex flex-col justify-between transition-all duration-200 ease-snappy-out hover:-translate-y-0.5 hover:border-[#e2916d]"
        >
          <div className="w-9 h-9 bg-[#332217] rounded-lg flex items-center justify-center">
            <Users className="w-[18px] h-[18px] text-[#e2916d]" />
          </div>
          <div className="mt-3">
            <p className="text-xs text-[#b9ad9b] font-medium">Total Students</p>
            <p className="text-xl font-bold text-[#f3ede2] tabular-nums">{formatNumber(stats?.totalStudents || 0)}</p>
          </div>
        </Link>

        {/* Total Teachers */}
        <Link
          to="/admin/teachers"
          className="col-span-12 md:col-span-3 bg-[#211c15] border border-[#3a3122] rounded-2xl p-5 flex flex-col justify-between transition-all duration-200 ease-snappy-out hover:-translate-y-0.5 hover:border-[#e2916d]"
        >
          <div className="w-9 h-9 bg-[#173322] rounded-lg flex items-center justify-center">
            <GraduationCap className="w-[18px] h-[18px] text-[#4ade80]" />
          </div>
          <div className="mt-3">
            <p className="text-xs text-[#b9ad9b] font-medium">Teachers</p>
            <p className="text-xl font-bold text-[#f3ede2] tabular-nums">{formatNumber(stats?.totalTeachers || 0)}</p>
          </div>
        </Link>

        {/* Pending Admissions */}
        <Link
          to="/admin/admissions"
          className="col-span-12 md:col-span-7 bg-[#211c15] border border-[#3a3122] rounded-2xl px-5 py-5 flex items-center justify-between transition-all duration-200 ease-snappy-out hover:-translate-y-0.5 hover:border-[#e2916d]"
        >
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-[#332711] rounded-lg flex items-center justify-center flex-shrink-0">
              <UserCheck className="w-[18px] h-[18px] text-[#f0b64c]" />
            </div>
            <div>
              <p className="text-xl font-bold text-[#f3ede2] tabular-nums">{formatNumber(stats?.pendingAdmissions || 0)}</p>
              <p className="text-xs text-[#b9ad9b]">Admissions awaiting your review</p>
            </div>
          </div>
          <span className="text-xs font-semibold text-[#e2916d] flex items-center gap-0.5 flex-shrink-0">
            Review now <ChevronRight className="w-3.5 h-3.5" />
          </span>
        </Link>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-12 gap-3.5">
        {/* Monthly Fee Collection Chart */}
        <div className="col-span-12 lg:col-span-8 bg-[#211c15] rounded-2xl border border-[#3a3122] p-5">
          <div className="flex items-center justify-between mb-5">
            <h3 className="text-sm font-semibold text-[#f3ede2]">Monthly Fee Collection</h3>
            <Link to="/admin/fees/collect" className="text-xs font-semibold text-[#e2916d] hover:text-[#f0a67d] flex items-center gap-0.5">
              View details <ChevronRight className="w-3.5 h-3.5" />
            </Link>
          </div>
          <div className="h-44">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={monthlyData} barCategoryGap="28%">
                <XAxis dataKey="month" stroke="#8a7f6e" fontSize={11} axisLine={false} tickLine={false} />
                <Tooltip
                  cursor={{ fill: '#28221a' }}
                  contentStyle={{
                    backgroundColor: '#211c15',
                    border: '1px solid #3a3122',
                    borderRadius: '10px',
                    fontSize: '12px',
                    color: '#f3ede2'
                  }}
                  labelStyle={{ color: '#f3ede2' }}
                  itemStyle={{ color: '#f3ede2' }}
                  formatter={(value: number) => [formatCurrency(value), 'Amount']}
                />
                <Bar dataKey="amount" radius={[6, 6, 3, 3]}>
                  {monthlyData.map((_, index) => (
                    <Cell key={index} fill={index === monthlyData.length - 1 ? '#e2916d' : '#332b20'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Fee Status Donut */}
        <div className="col-span-12 lg:col-span-4 bg-[#211c15] rounded-2xl border border-[#3a3122] p-5">
          <h3 className="text-sm font-semibold text-[#f3ede2] mb-5">Fee Status</h3>
          <div className="relative h-40">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={feeData}
                  cx="50%"
                  cy="50%"
                  innerRadius={52}
                  outerRadius={72}
                  paddingAngle={3}
                  dataKey="value"
                  stroke="none"
                >
                  {feeData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.fill} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#211c15',
                    border: '1px solid #3a3122',
                    borderRadius: '10px',
                    fontSize: '12px',
                    color: '#f3ede2'
                  }}
                  itemStyle={{ color: '#f3ede2' }}
                  formatter={(value: number) => formatCurrency(value)}
                />
              </PieChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <span className="text-xl font-bold text-[#f3ede2]">{collectedPct}%</span>
              <span className="text-[11px] text-[#b9ad9b]">collected</span>
            </div>
          </div>
          <div className="flex justify-center gap-4 mt-4 text-xs text-[#b9ad9b]">
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-sm bg-[#4ade80]"></span>
              {formatCurrency(stats?.totalFeeCollection || 0)} collected
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-sm bg-[#332b20]"></span>
              {formatCurrency(stats?.pendingFees || 0)} pending
            </span>
          </div>
        </div>
      </div>

      {/* Recent Admissions + Snapshot */}
      <div className="grid grid-cols-12 gap-3.5">
        <div className="col-span-12 lg:col-span-7 bg-[#211c15] rounded-2xl border border-[#3a3122] p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-[#f3ede2]">Recent Admissions</h3>
            <Link to="/admin/admissions" className="text-xs font-semibold text-[#e2916d] hover:text-[#f0a67d] flex items-center gap-0.5">
              See all <ChevronRight className="w-3.5 h-3.5" />
            </Link>
          </div>
          {recentRegistrations.length === 0 ? (
            <p className="text-sm text-[#b9ad9b] py-4">No admission requests yet.</p>
          ) : (
            <div>
              {recentRegistrations.map((reg, index) => (
                <div
                  key={reg.id}
                  className={`flex items-center gap-3 py-2.5 ${index !== recentRegistrations.length - 1 ? 'border-b border-[#3a3122]' : ''}`}
                >
                  <div className="w-8 h-8 bg-[#28221a] rounded-lg flex items-center justify-center text-xs font-bold text-[#b9ad9b] flex-shrink-0">
                    {getInitials(reg.name)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-[#f3ede2] truncate">{reg.name} &middot; Class {reg.preferredClass}</p>
                    <p className="text-xs text-[#8a7f6e]">{formatDistanceToNow(new Date(reg.createdAt), { addSuffix: true })}</p>
                  </div>
                  <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full flex-shrink-0 ${
                    reg.registrationStatus === 'Approved'
                      ? 'bg-[#173322] text-[#4ade80]'
                      : reg.registrationStatus === 'Rejected'
                      ? 'bg-[#341a17] text-[#f38b83]'
                      : 'bg-[#332711] text-[#f0b64c]'
                  }`}>
                    {reg.registrationStatus}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="col-span-12 lg:col-span-5 bg-[#211c15] rounded-2xl border border-[#3a3122] p-5">
          <h3 className="text-sm font-semibold text-[#f3ede2] mb-3">Fees & Notices Snapshot</h3>
          <div>
            <div className="flex items-center justify-between py-2.5 border-b border-[#3a3122]">
              <span className="text-sm text-[#b9ad9b] flex items-center gap-2">
                <IndianRupee className="w-3.5 h-3.5 text-[#8a7f6e]" />
                Total collected (all-time)
              </span>
              <span className="text-sm font-bold text-[#f3ede2] tabular-nums">{formatCurrency(stats?.totalFeeCollection || 0)}</span>
            </div>
            <div className="flex items-center justify-between py-2.5 border-b border-[#3a3122]">
              <span className="text-sm text-[#b9ad9b] flex items-center gap-2">
                <IndianRupee className="w-3.5 h-3.5 text-[#8a7f6e]" />
                Pending fees
              </span>
              <span className="text-sm font-bold text-[#f3ede2] tabular-nums">{formatCurrency(stats?.pendingFees || 0)}</span>
            </div>
            <div className="flex items-center justify-between py-2.5">
              <span className="text-sm text-[#b9ad9b] flex items-center gap-2">
                <Megaphone className="w-3.5 h-3.5 text-[#8a7f6e]" />
                Active notices
              </span>
              <span className="text-sm font-bold text-[#f3ede2] tabular-nums">{formatNumber(stats?.activeNotices || 0)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-[#8a7f6e] mb-3">Quick actions</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3.5">
          <Link
            to="/admin/registrations"
            className="bg-[#211c15] border border-[#3a3122] rounded-xl p-4 flex items-center gap-3 transition-all duration-200 ease-snappy-out hover:-translate-y-0.5 hover:border-[#e2916d] group"
          >
            <div className="w-9 h-9 bg-[#332217] rounded-lg flex items-center justify-center flex-shrink-0">
              <Users className="w-[18px] h-[18px] text-[#e2916d]" />
            </div>
            <div>
              <p className="text-sm font-semibold text-[#f3ede2]">New Registration</p>
              <p className="text-xs text-[#8a7f6e]">Start a student registration</p>
            </div>
          </Link>

          <Link
            to="/admin/teachers/new"
            className="bg-[#211c15] border border-[#3a3122] rounded-xl p-4 flex items-center gap-3 transition-all duration-200 ease-snappy-out hover:-translate-y-0.5 hover:border-[#e2916d] group"
          >
            <div className="w-9 h-9 bg-[#173322] rounded-lg flex items-center justify-center flex-shrink-0">
              <GraduationCap className="w-[18px] h-[18px] text-[#4ade80]" />
            </div>
            <div>
              <p className="text-sm font-semibold text-[#f3ede2]">Add Teacher</p>
              <p className="text-xs text-[#8a7f6e]">Add new teacher</p>
            </div>
          </Link>

          <Link
            to="/admin/fees/collect"
            className="bg-[#211c15] border border-[#3a3122] rounded-xl p-4 flex items-center gap-3 transition-all duration-200 ease-snappy-out hover:-translate-y-0.5 hover:border-[#e2916d] group"
          >
            <div className="w-9 h-9 bg-[#332711] rounded-lg flex items-center justify-center flex-shrink-0">
              <IndianRupee className="w-[18px] h-[18px] text-[#f0b64c]" />
            </div>
            <div>
              <p className="text-sm font-semibold text-[#f3ede2]">Collect Fee</p>
              <p className="text-xs text-[#8a7f6e]">Process payment</p>
            </div>
          </Link>

          <Link
            to="/admin/notifications/notices"
            className="bg-[#211c15] border border-[#3a3122] rounded-xl p-4 flex items-center gap-3 transition-all duration-200 ease-snappy-out hover:-translate-y-0.5 hover:border-[#e2916d] group"
          >
            <div className="w-9 h-9 bg-[#332b20] rounded-lg flex items-center justify-center flex-shrink-0">
              <ClipboardList className="w-[18px] h-[18px] text-[#b9ad9b]" />
            </div>
            <div>
              <p className="text-sm font-semibold text-[#f3ede2]">Post Notice</p>
              <p className="text-xs text-[#8a7f6e]">Create announcement</p>
            </div>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
